import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  HairlineCard,
  Stack,
  useSnackbar,
} from '@munifocus/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  ComplainantRelationship,
  ComplaintDetail,
  ExtractionMismatch,
  FileDetail,
  IntakeMethod,
  OwnerType,
  PropertyType,
  SnbDocumentKind,
} from '@munifocus/contracts-gov';
import { countExtractedFields, mergeRecordParcels } from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { FileFormFields, needsSpecify } from '../components/file-form/FileFormFields';
import {
  fileFormGateMessages,
  EMPTY_FILE_FORM,
  useFileForm,
  type FileFormValues,
} from '../components/file-form/useFileForm';
import { useSubmitValidation } from '../lib/useSubmitValidation';
import { SnbUploadPanel } from '../components/tombstone/SnbUploadPanel';
import { SnbSourceGuide } from '../components/tombstone/SnbSourceGuide';
import { useComplaint, useFile, useUpdateComplaint, useUpdateFile } from '../lib/files-api';
import { isPlaceholderName } from '../lib/file-display';
import { getDraft, useExtractForFile, useExtractions } from '../lib/property-extraction-api';
import { FileLoadError } from '../components/FileLoadError';

/** The stored file and complaint, as the one form sees them. */
function toFormValues(file: FileDetail, complaint: ComplaintDetail | undefined): FileFormValues {
  return {
    ...EMPTY_FILE_FORM,
    intakeMethod: (complaint?.intakeMethod ?? '') as IntakeMethod | '',
    intakeMethodOther: complaint?.intakeMethodOther ?? '',
    anonymous: complaint?.anonymous ?? false,
    complainantName: isPlaceholderName(complaint?.complainantName)
      ? ''
      : (complaint?.complainantName ?? ''),
    complainantEmail: complaint?.complainantEmail ?? '',
    complainantPhone: complaint?.complainantPhone ?? '',
    complainantAddress: complaint?.complainantAddress ?? '',
    complainantCity: complaint?.complainantCity ?? '',
    complainantProvince: complaint?.complainantProvince ?? '',
    complainantPostalCode: complaint?.complainantPostalCode ?? '',
    complainantRelationship: (complaint?.complainantRelationship ?? '') as
      ComplainantRelationship | '',
    complainantRelationshipOther: complaint?.complainantRelationshipOther ?? '',
    complainantLocale: complaint?.complainantLocale === 'fr' ? 'fr' : 'en',
    referringPerson: complaint?.referringPerson ?? '',
    internalContext: complaint?.internalContext ?? '',
    address: file.address ?? '',
    localGovernmentId: file.localGovernmentId ?? '',
    assignedOfficerId: file.assignedOfficerId ?? '',
    propertyType: (file.propertyType ?? '') as PropertyType | '',
    propertyTypeOther: file.propertyTypeOther ?? '',
    bylawCategory: file.bylawCategory,
    enforcedLawId: file.enforcedLawId ?? '',
    bylawCategoryOther: file.bylawCategoryOther ?? '',
    priority: file.priority,
    priorityReason: file.priorityReason ?? '',
    priorityReasonOther: file.priorityReasonOther ?? '',
    // The description is the complaint's; the file has `notes` for its own.
    description: complaint?.description ?? '',
    pan: file.pan ?? '',
    county: file.county ?? '',
    parcelRows:
      file.parcels && file.parcels.length > 0
        ? file.parcels.map((p) => ({
            id: p.id,
            pid: p.pid,
            isPrimary: p.isPrimary,
            county: p.county ?? undefined,
            area: p.area ?? undefined,
            areaUnit: p.areaUnit ?? undefined,
            landTitlesStatus: p.landTitlesStatus ?? undefined,
            landGazette: p.landGazette ?? undefined,
            interestType: p.interestType ?? undefined,
            civicNumber: p.civicNumber ?? undefined,
            streetName: p.streetName ?? undefined,
            streetType: p.streetType ?? undefined,
            streetDirection: p.streetDirection ?? undefined,
            placeName: p.placeName ?? undefined,
            parish: p.parish ?? undefined,
          }))
        : [{ pid: file.propertyId ?? '', isPrimary: true }],
    ownerName: file.ownerName ?? '',
    ownerEmail: file.ownerEmail ?? '',
    ownerPhone: file.ownerPhone ?? '',
    occupantName: file.occupantName ?? '',
    occupantPhone: file.occupantPhone ?? '',
    occupantAddress: file.occupantAddress ?? '',
    occupantCity: file.occupantCity ?? '',
    occupantProvince: file.occupantProvince ?? '',
    occupantPostalCode: file.occupantPostalCode ?? '',
    ownerLocale: (file.ownerLocale ?? '') as 'en' | 'fr' | '',
    ownerType: (file.ownerType ?? '') as OwnerType | '',
    ownerTypeOther: file.ownerTypeOther ?? '',
    mailingAddress: file.mailingAddress ?? '',
    harmonizationStatus: file.harmonizationStatus ?? '',
    ownerUnknown: file.ownerUnknown ?? false,
    propertyDescription: file.propertyDescription ?? '',
    taxingAuthority: file.taxingAuthority ?? '',
    postalCode: file.postalCode ?? '',
    taxClass: file.taxClass ?? '',
    neighbourhood: file.neighbourhood ?? '',
  };
}

/**
 * Edit a file — the same form that created it.
 *
 * There is one set of fields a file is made of, so there is one screen for them
 * whether the file exists yet or not. It writes to two endpoints because the
 * record is stored in two tables (the complaint that opened the file, and the
 * file itself), which is an implementation detail the officer should never have
 * had to learn by being handed two different edit screens.
 *
 * Service New Brunswick records are the exception and stay on their own screen:
 * uploading one is not editing a field, it re-reads the property from a
 * document and logs every value it changes.
 */
export function EditFile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id = '' } = useParams();

  const file = useFile(id);
  const complaint = useComplaint(file.data?.complaintId);
  const saveFile = useUpdateFile(id);
  const saveComplaint = useUpdateComplaint(file.data?.complaintId ?? '');

  const form = useFileForm();
  const [seeded, setSeeded] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  // SNB records upload against the file directly here — it already exists, so
  // there is nothing to stage. Which kinds have been read is what the panel
  // shows as done, and the file's own extraction history is the source of that.
  const extract = useExtractForFile(id);
  // Once the file has loaded; a file out of reach would only 404 here as well.
  const extractions = useExtractions(file.data ? id : undefined);
  const [mismatches, setMismatches] = useState<ExtractionMismatch[]>([]);
  const readKinds = [
    ...new Set(
      (extractions.data ?? [])
        .filter((run) => run.status === 'succeeded')
        .map((run) => run.kind as SnbDocumentKind),
    ),
  ];

  // Seed once both halves have settled. Seeding on the file alone would blank
  // every complaint field and then read as the officer having cleared them.
  useEffect(() => {
    if (seeded || !file.data) return;
    if (file.data.complaintId && complaint.isLoading) return;
    form.reset(toFormValues(file.data, complaint.data));
    setSeeded(true);
  }, [complaint.data, complaint.isLoading, file.data, form, seeded]);

  // What was reported is frozen once an officer has confirmed intake and the
  // file has moved on: downstream work, and eventually a served notice, rests
  // on it. The property and owner stay editable — title research continues.
  const complaintLocked = !!file.data && file.data.stage !== 'intake';
  // A notice that has been served names an owner and an address. Correcting
  // them afterwards is sometimes right and always worth knowing about, because
  // the file and the delivered document now disagree.
  const step5 = (file.data?.stageData?.step_5 ?? {}) as {
    serviceMethod?: string;
    serviceDate?: string;
  };
  const served = Boolean(step5.serviceMethod && step5.serviceDate);
  // Only once seeded — before that the form still holds the empty default and
  // every anchored file would read as being un-anchored.
  const reanchoring =
    seeded && !!file.data && (file.data.localGovernmentId ?? '') !== form.values.localGovernmentId;

  const backToFile = () => navigate(`/files/${id}`);
  const requestLeave = () => (form.dirty ? setConfirmLeave(true) : backToFile());

  const { values } = form;
  const saving = saveFile.isPending || saveComplaint.isPending;

  const v = useSubmitValidation(fileFormGateMessages(form, t));

  const submit = async () => {
    if (!form.canSubmit || !file.data) return;
    try {
      // The complaint first: if the two writes disagree about which method a
      // file was opened under, the gating that follows from it should already
      // have been applied when the file's own fields land.
      if (file.data.complaintId && !complaintLocked) {
        await saveComplaint.mutateAsync({
          intakeMethod: values.intakeMethod || undefined,
          intakeMethodOther: needsSpecify(values.intakeMethod)
            ? values.intakeMethodOther.trim()
            : '',
          referringPerson: form.showInternalSource ? values.referringPerson.trim() : '',
          internalContext: form.showInternalSource ? values.internalContext.trim() : '',
          anonymous: form.showComplainant ? values.anonymous : false,
          complainantName: values.complainantName.trim(),
          complainantEmail: values.complainantEmail.trim(),
          complainantPhone: form.showComplainant ? values.complainantPhone.trim() : '',
          complainantAddress: form.showComplainant ? values.complainantAddress.trim() : '',
          complainantCity: form.showComplainant ? values.complainantCity.trim() : '',
          complainantProvince: form.showComplainant ? values.complainantProvince.trim() : '',
          complainantPostalCode: form.showComplainant ? values.complainantPostalCode.trim() : '',
          complainantRelationship: form.showComplainant
            ? values.complainantRelationship || undefined
            : undefined,
          complainantRelationshipOther:
            form.showComplainant && needsSpecify(values.complainantRelationship)
              ? values.complainantRelationshipOther.trim()
              : '',
          complainantLocale: values.complainantLocale,
          description: values.description.trim(),
        });
      }

      await saveFile.mutateAsync({
        address: values.address.trim(),
        // `null`, not omitted, when cleared: omitting the key leaves the file
        // anchored, and un-anchoring is a correction an officer has to be able
        // to make (a property that turns out to be in an LSD).
        localGovernmentId: values.localGovernmentId || null,
        // Sent only when it actually changed. Echoing the stored assignee back
        // makes the patch *assert* them, and a re-anchor that moves the file out
        // from under them would then be rejected instead of clearing the
        // assignment — turning a legitimate correction into an error.
        ...(values.assignedOfficerId !== (file.data.assignedOfficerId ?? '')
          ? { assignedOfficerId: values.assignedOfficerId || null }
          : {}),
        propertyType: values.propertyType || undefined,
        propertyTypeOther: needsSpecify(values.propertyType) ? values.propertyTypeOther.trim() : '',
        bylawCategory: values.bylawCategory,
        // '' means "not recorded"; the API takes an absent value as no change,
        // so clearing it needs an explicit null rather than an empty string.
        enforcedLawId: values.enforcedLawId || undefined,
        bylawCategoryOther: needsSpecify(values.bylawCategory)
          ? values.bylawCategoryOther.trim()
          : '',
        priority: values.priority,
        priorityReason: form.showPriorityReason ? values.priorityReason || undefined : undefined,
        priorityReasonOther:
          form.showPriorityReason && needsSpecify(values.priorityReason)
            ? values.priorityReasonOther.trim()
            : '',
        pan: values.pan.trim(),
        county: values.county.trim(),
        parcels: form.parcels,
        ownerName: values.ownerName.trim(),
        ownerEmail: !values.ownerUnknown ? values.ownerEmail.trim() || undefined : undefined,
        ownerPhone: !values.ownerUnknown ? values.ownerPhone.trim() : '',
        occupantName: values.occupantName.trim(),
        occupantPhone: values.occupantPhone.trim(),
        occupantAddress: values.occupantAddress.trim(),
        occupantCity: values.occupantCity.trim(),
        occupantProvince: values.occupantProvince.trim(),
        occupantPostalCode: values.occupantPostalCode.trim(),
        ownerLocale: !values.ownerUnknown ? values.ownerLocale || undefined : undefined,
        ownerType: values.ownerType || undefined,
        ownerTypeOther: needsSpecify(values.ownerType) ? values.ownerTypeOther.trim() : '',
        mailingAddress: values.mailingAddress.trim(),
        harmonizationStatus: values.harmonizationStatus.trim(),
        propertyDescription: values.propertyDescription.trim(),
        taxingAuthority: values.taxingAuthority.trim(),
        postalCode: values.postalCode.trim(),
        taxClass: values.taxClass.trim(),
        neighbourhood: values.neighbourhood.trim(),
        ownerUnknown: values.ownerUnknown,
      });

      // Name the file: an officer working three of them needs to know which
      // one just took the change, not that "a" file did.
      snackbar.success(
        t('gov.file.edit.saved', {
          reference: file.data.reference,
          address: values.address.trim(),
        }),
      );
      backToFile();
    } catch {
      snackbar.error(t('gov.file.edit.saveError'));
    }
  };

  // The failure first: the form is seeded only from a loaded file, so with the
  // file out of reach the loading branch below used to spin forever.
  if (file.isError) {
    return (
      <AppShell>
        <FileLoadError error={file.error} />
      </AppShell>
    );
  }

  if (file.isLoading || !seeded || !file.data) {
    return (
      <AppShell>
        <CircularProgress />
      </AppShell>
    );
  }

  return (
    <AppShell
      back={{ label: file.data.reference, onClick: requestLeave }}
      title={t('gov.file.edit.title')}
      bottomBar={
        <Stack direction="row" spacing={1}>
          <Button fullWidth variant="outlined" disabled={saving} onClick={requestLeave}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            fullWidth
            variant="contained"
            // Nothing changed is nothing to save; a missing field is answered
            // at the field instead of by a dead button.
            disabled={saving || !form.dirty}
            onClick={v.guard(() => void submit())}
          >
            {saving ? t('common.actions.saving') : t('common.actions.save')}
          </Button>
        </Stack>
      }
    >
      <PageIntro>{t('gov.file.edit.subtitle')}</PageIntro>

      {!file.data.complaintId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('gov.intake.edit.noComplaint')}
        </Alert>
      )}

      {complaintLocked && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('gov.file.edit.complaintLocked')}
        </Alert>
      )}

      {served && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('gov.file.edit.servedWarning')}
        </Alert>
      )}

      {/* An officer's authority does not follow a file across a municipal
          boundary: the API clears an assignment the new local government does
          not back, rather than blocking the correction. Saying so before the
          save is the difference between a documented consequence and a file
          that quietly lost its officer. */}
      {reanchoring && file.data.assignedOfficerId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('gov.file.edit.reanchorWarning')}
        </Alert>
      )}

      <FileFormFields
        form={form}
        validation={v}
        complaintLocked={complaintLocked}
        snb={
          /* The same upload the intake form offers, against the file rather
             than a staging key. Sending the officer to another screen to
             re-read a document they are already looking at the fields of was
             the wrong answer. */
          <HairlineCard sx={{ p: { xs: 2, md: 2.5 } }}>
            <SnbUploadPanel
              done={readKinds}
              onUpload={async (document, kind) => {
                const run = await extract.mutateAsync({ document, kind });
                // The run reports what this document said; the draft is every
                // record on the file merged, which is what the fields want.
                const d = await getDraft(id);
                setMismatches(d.mismatches);
                // Fill blanks only — a value already on the file was put there
                // by a person and must not be overwritten by a document.
                form.fillIfBlank('address', d.address);
                form.fillIfBlank('pan', d.pan);
                form.fillIfBlank('county', d.county);
                form.fillIfBlank('ownerName', d.ownerName);
                form.fillIfBlank('ownerType', (d.ownerType ?? '') as OwnerType);
                form.fillIfBlank('mailingAddress', d.mailingAddress);
                form.fillIfBlank('harmonizationStatus', d.harmonizationStatus);
                form.fillIfBlank('propertyDescription', d.propertyDescription);
                form.fillIfBlank('taxingAuthority', d.taxingAuthority);
                form.fillIfBlank('postalCode', d.postalCode);
                form.fillIfBlank('taxClass', d.taxClass);
                form.fillIfBlank('neighbourhood', d.neighbourhood);
                // Every PID the PAN lists joins the ones already on the file.
                if (d.parcels.length > 0) {
                  form.setParcelRows(mergeRecordParcels(form.values.parcelRows, d.parcels));
                }
                return {
                  fieldsRead: countExtractedFields(run.extracted),
                  warnings: run.warnings,
                };
              }}
            />

            <Divider sx={{ my: 1.5 }} />

            <SnbSourceGuide extracted={readKinds} mismatches={mismatches} />

            {/* Provenance — what each record was read as, and every value since
                saved differently — is a history, and lives with the history. */}
            <Stack direction="row" sx={{ mt: 1.5 }}>
              <Button size="small" onClick={() => navigate(`/files/${id}/tombstone`)}>
                {t('gov.file.edit.snbHistory')}
              </Button>
            </Stack>
          </HairlineCard>
        }
      />

      <Dialog open={confirmLeave} onClose={() => setConfirmLeave(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('gov.file.edit.leaveTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('gov.file.edit.leaveBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLeave(false)}>{t('gov.intake.leave.stay')}</Button>
          <Button color="error" onClick={backToFile}>
            {t('gov.intake.leave.discard')}
          </Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
}
